<!--
@component
The button. Use for every clickable action, and for links styled as actions (`href`). `variant` sets emphasis (`primary` outlined, `secondary` borderless), `color` sets intent (`primary`, `accent`, `danger`, `warning`), `size` is `regular` or `small`. `loading` disables it and shows a spinner without changing its width.

@use-instead-of raw <button>/<a> styled with btn-* or ad-hoc classes; a Loading placed next to a Button; Cta
@figma https://www.figma.com/design/xb1BaksrwtGhe3JHzX1BbT/Design-System?node-id=2376-9104
@see ButtonGroup, ClickableIcon, CopyButton
@example <Button color="danger" iconLeading={Trash2} onclick={remove}>Delete</Button>
-->
<script lang="ts">
  import type { LucideIcon as IconType } from "@lucide/svelte";
  import clsx from "clsx";
  import type { Snippet } from "svelte";
  import { getContext } from "svelte";
  import type { SvelteHTMLElements } from "svelte/elements";

  import type { TooltipProps } from "$lib/design-systems/product/primitives/overlays/tooltip";
  import tooltip from "$lib/design-systems/product/primitives/overlays/tooltip";
  import {
    BUTTON_GROUP_CONTEXT,
    type ButtonGroupContext,
  } from "./ButtonGroup.svelte";
  import Loading from "../feedback/Loading.svelte";

  /** The button variant.
   * - Primary: high emphasis CTA with border and translucent background
   * - Secondary: medium emphasis, no border or background
   */
  type ButtonVariant = "primary" | "secondary";
  export type ButtonColor = "primary" | "accent" | "danger" | "warning";
  export type ButtonSize = "regular" | "small";

  interface SharedProps {
    /** Emphasis: `primary` is outlined, `secondary` is plain text. */
    variant?: ButtonVariant;
    /** Palette for text and tinted backgrounds, independent of `variant`. */
    color?: ButtonColor;
    /** Padding and font size preset. */
    size?: ButtonSize;
    iconLeading?: IconType;
    iconTrailing?: IconType;
    class?: string;
    iconClass?: string;
    tooltip?: string | TooltipProps | false;
    children?: Snippet;
    element?: HTMLElement /* Needed for Svelte binding */;
    /** Disables the button and swaps its icon for a spinner (overlaid on label-only buttons) without changing its width. */
    loading?: boolean;
  }

  type ClassMap<K extends string | undefined> = Record<
    NonNullable<K>,
    string | undefined
  >;

  const variantClass: ClassMap<ButtonVariant> = {
    primary: "btn-outlined",
    secondary: undefined,
  };

  const colorClass: ClassMap<ButtonColor> = {
    primary: undefined,
    accent: "btn-accent",
    danger: "btn-danger",
    warning: "btn-warning",
  };

  const sizeClass: ClassMap<ButtonSize> = {
    regular: "",
    small: "btn-sm",
  };

  export type Props =
    | (SharedProps & SvelteHTMLElements["a"] & { href: string })
    | (SharedProps & SvelteHTMLElements["button"] & { href?: never });

  let {
    variant = "primary",
    color = "primary",
    size: sizeOverride,
    iconLeading,
    iconTrailing,
    class: classOverride,
    iconClass: iconClassOverride,
    children,
    tooltip: tooltipProps,
    element = $bindable(),
    loading = false,
    ...rest
  }: Props = $props();

  // An explicit `size` wins; otherwise inherit from a wrapping `ButtonGroup`,
  // falling back to "regular" for a standalone button.
  const buttonGroup = getContext<ButtonGroupContext | undefined>(
    BUTTON_GROUP_CONTEXT,
  );
  let size = $derived(sizeOverride ?? buttonGroup?.size ?? "regular");
  let iconOnly = $derived(!children && (iconLeading || iconTrailing));
  let tag = $derived(rest.href ? "a" : "button");
  let tagAttrs = $derived(tag === "a" ? {} : { type: "button" as const });
  let loadingAttrs = $derived.by(() => {
    if (!loading) return {};
    if (tag === "button") return { "aria-busy": true, disabled: true };
    return {
      "aria-busy": true,
      "aria-disabled": true,
      tabindex: -1,
      onclick: (event: MouseEvent) => event.preventDefault(),
    };
  });
  // The spinner takes the place of an icon so the width doesn't change. A
  // label-only button has no icon to swap, so the spinner is overlaid on the
  // hidden label instead.
  let spinnerReplacesLeading = $derived(loading && !!iconLeading);
  let spinnerReplacesTrailing = $derived(
    loading && !iconLeading && !!iconTrailing,
  );
  let spinnerOverlaid = $derived(loading && !iconLeading && !iconTrailing);
</script>

{#snippet icon(icon: IconType)}
  {@const Icon = icon}
  <Icon
    size={size === "regular" ? 16 : 12}
    class={clsx(["icon", iconClassOverride])}
  />
{/snippet}

{#snippet spinner()}
  <Loading
    class={clsx([
      "icon shrink-0",
      size === "regular" ? "size-4" : "size-3",
      iconClassOverride,
    ])}
  />
{/snippet}

<svelte:element
  this={tag}
  bind:this={element}
  use:tooltip={tooltipProps}
  class={[
    "btn",
    variantClass[variant],
    colorClass[color],
    sizeClass[size],
    iconOnly && "icon-only",
    spinnerOverlaid && "relative",
    loading && tag === "a" && "pointer-events-none",
    classOverride,
  ]}
  {...tagAttrs}
  {...rest}
  {...loadingAttrs}
>
  {#if spinnerReplacesLeading}
    {@render spinner()}
  {:else if iconLeading}
    {@render icon(iconLeading)}
  {/if}
  <span class="contents" class:invisible={spinnerOverlaid}>
    {@render children?.()}
  </span>
  {#if spinnerReplacesTrailing}
    {@render spinner()}
  {:else if iconTrailing}
    {@render icon(iconTrailing)}
  {/if}
  {#if spinnerOverlaid}
    <span class="absolute inset-0 flex items-center justify-center">
      {@render spinner()}
    </span>
  {/if}
</svelte:element>

<style lang="postcss">
  @reference "$app.css";

  @layer components {
    .btn {
      --btn-fg: var(--semantic-text-primary);
      --btn-ring: var(--semantic-border-primary);
      --btn-hover: var(--semantic-background-primary-hover);
      --btn-active: var(--semantic-background-primary-active);
      --btn-border: var(--semantic-border-primary);
      --btn-bg: var(--semantic-background-primary);
      --btn-icon: var(--semantic-icon-primary);

      color: var(--btn-fg);
      --tw-ring-color: color-mix(in oklch, var(--btn-ring) 80%, transparent);
      @apply border border-transparent; /* Invisible border so outlined and non-outlined variants are the same size. */
      @apply inline-flex items-center justify-center gap-x-2 rounded-md px-2 py-1.5 whitespace-nowrap;
      @apply font-sans text-sm font-medium transition-colors;
      @apply focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50;

      &.btn-sm {
        @apply gap-x-1 rounded px-1.5 py-0.5 text-xs;
      }

      &.btn-outlined {
        border-color: var(--btn-border);
        background-color: var(--btn-bg);
      }

      &:hover:not(:disabled),
      &[data-test-pseudo~="hover"] {
        background-color: var(--btn-hover);
      }

      &:active:not(:disabled),
      &[data-test-pseudo~="active"] {
        background-color: var(--btn-active);
      }

      &:focus-visible,
      &[data-test-pseudo~="focus-visible"] {
        @apply ring-3;
      }

      /* global is needed to apply to the svg exported by lucide */
      &.icon-only {
        /* An icon (16px) is shorter than a text line (20px), so pin the height
           to match a text button so icon-only buttons align in a row. */
        @apply min-h-8.5;

        &.btn-sm {
          @apply min-h-5.5; /* Match the height of a small text button. */
        }
      }

      :global(.icon) {
        color: var(--btn-icon);
      }

      /* :global() prevents Svelte from stripping these as "unused CSS" —
         the class names come from a dynamic object lookup (colorClass[color])
         that the compiler can't statically trace. */
      &:global(.btn-accent) {
        --btn-fg: var(--semantic-text-accent);
        --btn-ring: var(--semantic-border-accent);
        --btn-hover: var(--semantic-background-accent-hover);
        --btn-active: var(--semantic-background-accent-active);
        --btn-border: var(--semantic-border-accent);
        --btn-bg: var(--semantic-background-accent);
        --btn-icon: var(--semantic-icon-accent);
      }

      &:global(.btn-danger) {
        --btn-fg: var(--semantic-text-danger);
        --btn-ring: var(--semantic-border-danger);
        --btn-hover: var(--semantic-background-danger-hover);
        --btn-active: var(--semantic-background-danger-active);
        --btn-border: var(--semantic-border-danger);
        --btn-bg: var(--semantic-background-danger);
        --btn-icon: var(--semantic-icon-danger);
      }

      &:global(.btn-warning) {
        --btn-fg: var(--semantic-text-warning);
        --btn-ring: var(--semantic-border-warning);
        --btn-hover: var(--semantic-background-warning-hover);
        --btn-active: var(--semantic-background-warning-active);
        --btn-border: var(--semantic-border-warning);
        --btn-bg: var(--semantic-background-warning);
        --btn-icon: var(--semantic-icon-warning);
      }
    }
  }
</style>
